// Phase RV-3 — RolloverTrackerSummary.
//
// Per-cycle list. Each row: cycle id, status badge, slip summary,
// candidates-scanned count, expand chevron.
//
// Doctrine:
//   - N08: SKIPPED uses `ghost` (grey) variant; LOST uses `danger`
//     (red). Distinct, never the same.
//   - N12: skip cycles are framed as "Standard working as intended"
//     by the expanded Intelligence view; this summary row just shows
//     the SKIPPED badge and the candidates-scanned count (free).
//   - Free users see: cycle id, status, candidates-scanned. That is
//     the §08 Table 23 free-tier transparency contract.
//
// Visual: Football Products (FpKit) Kalshi-clean. Inline styles, FpKit
// tokens, hairline borders, Bebas numbers, mono uppercase micro-labels.

import type { CSSProperties, ReactNode } from "react";
import type {
  SlipStatus,
  SlipOutcome,
  TrackerCycle,
  WindowLabel,
} from "../../rollover/useRolloverTracker.ts";

const WINDOW_LABEL: Record<WindowLabel, string> = {
  midweek: "Midweek",
  weekend: "Weekend",
  supplement: "Supplement",
};

export type BadgeVariant = "amber" | "success" | "danger" | "warn" | "info" | "ghost";

interface BadgeSpec {
  variant: BadgeVariant;
  label: string;
}

export function statusBadge(status: SlipStatus, outcome: SlipOutcome | null): BadgeSpec {
  if (status === "skipped" || outcome === "skipped") {
    return { variant: "ghost", label: "SKIPPED" };
  }
  if (outcome === "won") return { variant: "success", label: "WON" };
  if (outcome === "lost") return { variant: "danger", label: "LOST" };
  if (outcome === "void") return { variant: "info", label: "VOID" };
  if (status === "settled") return { variant: "info", label: "SETTLED" };
  if (status === "published" || status === "assembled")
    return { variant: "amber", label: "ACTIVE" };
  if (status === "rebalance_required")
    return { variant: "warn", label: "REBALANCE" };
  return { variant: "ghost", label: "PENDING" };
}

// --- FpKit inline primitives -------------------------------------------

const BADGE_VARIANTS: Record<BadgeVariant, { bg: string; color: string; border: string }> = {
  amber: { bg: "var(--fp-accent-soft)", color: "var(--fp-accent-text)", border: "1px solid rgba(212,175,55,0.25)" },
  success: { bg: "var(--fp-won-soft)", color: "var(--fp-won)", border: "1px solid var(--fp-won-soft)" },
  danger: { bg: "var(--fp-lost-soft)", color: "var(--fp-lost)", border: "1px solid var(--fp-lost-soft)" },
  warn: { bg: "var(--fp-accent-soft)", color: "var(--fp-accent-text)", border: "1px solid rgba(212,175,55,0.25)" },
  info: { bg: "var(--bg-inset)", color: "var(--text-tertiary)", border: "1px solid var(--border-faint)" },
  ghost: { bg: "transparent", color: "var(--text-tertiary)", border: "1px solid var(--border-faint)" },
};

function FpBadge({ variant, children }: { variant: BadgeVariant; children: ReactNode }): JSX.Element {
  const v = BADGE_VARIANTS[variant];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "2px 7px",
        borderRadius: 4,
        background: v.bg,
        color: v.color,
        border: v.border,
      }}
    >
      {children}
    </span>
  );
}

function FpPill({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 4,
        border: "1px solid var(--border-faint)",
        background: "var(--bg-inset)",
        color: "var(--text-tertiary)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.12em",
        lineHeight: 1,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function FpEyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--fp-accent-text)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        ...style,
      }}
    >
      <span aria-hidden style={{ width: 16, height: 1, background: "currentColor", opacity: 0.7 }} />
      {children}
    </div>
  );
}

export interface RolloverTrackerSummaryProps {
  cycles: TrackerCycle[];
  expandedRunId?: string | null;
  onToggle?: (run_id: string) => void;
  renderExpanded?: (cycle: TrackerCycle) => ReactNode;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

function formatOdds(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}×`;
}

function shortCycleId(c: TrackerCycle, indexFromEnd: number): string {
  const winLabel = WINDOW_LABEL[c.window_label];
  return `Cycle ${indexFromEnd} · ${winLabel}`;
}

const cardStyle: CSSProperties = {
  background: "var(--bg-elev)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  overflow: "hidden",
};

const stateNoteStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11.5,
  color: "var(--text-tertiary)",
  marginTop: 10,
  lineHeight: 1.5,
};

export function RolloverTrackerSummary({
  cycles,
  expandedRunId,
  onToggle,
  renderExpanded,
  loading,
  error,
  emptyMessage,
}: RolloverTrackerSummaryProps): JSX.Element {
  if (loading) {
    return (
      <div style={{ ...cardStyle, padding: 14 }}>
        <FpEyebrow>Selection Tracker</FpEyebrow>
        <div style={stateNoteStyle}>Loading tracker…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...cardStyle, padding: 14 }}>
        <FpEyebrow>Selection Tracker</FpEyebrow>
        <div style={stateNoteStyle}>Tracker unavailable. {error}</div>
      </div>
    );
  }
  if (cycles.length === 0) {
    return (
      <div style={{ ...cardStyle, padding: 14 }}>
        <FpEyebrow>Selection Tracker</FpEyebrow>
        <div style={stateNoteStyle}>
          {emptyMessage ?? "No cycles yet. The first cycle appears after the next pipeline run."}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "12px 14px 10px",
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <FpEyebrow>Selection Tracker · last {cycles.length} cycles</FpEyebrow>
      </div>
      {cycles.map((c, idx) => {
        const indexFromEnd = cycles.length - idx;
        const slip = c.slip;
        const badge = statusBadge(slip?.status ?? "skipped", slip?.outcome ?? null);
        const isExpanded = expandedRunId === c.run_id;
        const totalScanned = c.candidates.total_scanned;
        const legCount = slip?.leg_count ?? 0;
        const combined = formatOdds(slip?.combined_odds ?? null);
        const tierLabel = c.tier.toUpperCase();

        return (
          <div
            key={c.run_id}
            style={{
              padding: "0 14px",
              borderTop: idx === 0 ? "none" : "1px solid var(--border-faint)",
            }}
          >
            <button
              type="button"
              onClick={onToggle ? () => onToggle(c.run_id) : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "12px 0",
                cursor: onToggle ? "pointer" : "default",
                textAlign: "left",
                color: "inherit",
              }}
              aria-expanded={isExpanded}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: 16,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "var(--text)",
                      lineHeight: 1,
                    }}
                  >
                    {shortCycleId(c, indexFromEnd)}
                  </span>
                  <FpPill>{tierLabel}</FpPill>
                  <FpBadge variant={badge.variant}>{badge.label}</FpBadge>
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    marginTop: 6,
                  }}
                >
                  {slip && slip.status !== "skipped"
                    ? `${legCount} legs · ${combined} · ${badge.label}`
                    : "No qualifying combination — quality gate held."}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9.5,
                    color: "var(--text-quaternary)",
                    marginTop: 4,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                  data-test="candidates-scanned"
                >
                  {`${totalScanned} candidates scanned`}
                </div>
              </div>
              <div
                aria-hidden
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--text-tertiary)",
                  paddingLeft: 8,
                }}
              >
                {isExpanded ? "▾" : "▸"}
              </div>
            </button>
            {isExpanded && renderExpanded ? (
              <div style={{ padding: "4px 0 16px" }}>{renderExpanded(c)}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// Small helper exposed for tests / footer consumers.
export function avgCombinedOdds(cycles: TrackerCycle[]): number | null {
  const odds = cycles
    .map((c) => c.slip?.combined_odds ?? null)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (odds.length === 0) return null;
  const sum = odds.reduce<number>((a, b) => a + b, 0);
  return sum / odds.length;
}

export function avgConfidence(cycles: TrackerCycle[]): number | null {
  const vals = cycles
    .map((c) => c.slip?.avg_confidence ?? null)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (vals.length === 0) return null;
  const sum = vals.reduce<number>((a, b) => a + b, 0);
  return sum / vals.length;
}

export function bestCycle(cycles: TrackerCycle[]): TrackerCycle | null {
  const wins = cycles.filter(
    (c) => c.slip?.outcome === "won" && typeof c.slip?.combined_odds === "number",
  );
  const first = wins[0];
  if (!first) return null;
  return wins.reduce<TrackerCycle>((best, cur) => {
    const a = best.slip?.combined_odds ?? 0;
    const b = cur.slip?.combined_odds ?? 0;
    return b > a ? cur : best;
  }, first);
}

export function longestLossStreak(cycles: TrackerCycle[]): number {
  // cycles arrive newest-first; walk in chronological order.
  const chrono = [...cycles].reverse();
  let cur = 0;
  let max = 0;
  for (const c of chrono) {
    if (c.slip?.outcome === "lost") {
      cur += 1;
      if (cur > max) max = cur;
    } else {
      cur = 0;
    }
  }
  return max;
}
